import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import {
  notificationMetadata,
  useMarkStaffNotificationRead,
  useStaffNotifications,
  useStaffNotificationsRealtime,
} from '@/hooks/useStaffNotifications';
import { staffNotificationHref } from '@/lib/patientEmail';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { formatMessageDateTime } from '@/utils/messageSender';

export function StaffNotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const { data: items = [] } = useStaffNotifications(activeClinicId);
  const markRead = useMarkStaffNotificationRead(activeClinicId);
  useStaffNotificationsRealtime(activeClinicId);

  const unread = useMemo(() => items.filter((row) => !row.read_at), [items]);

  return (
    <MenuPopover
      align="start"
      title={t('nav.notifications', 'Notifikationer')}
      className="bottom-full mb-s2 mt-0 min-w-0 w-full"
      trigger={
        <button
          type="button"
          className={cn(
            TYPE.control,
            'relative flex h-10 w-full items-center gap-s3 rounded-ctl px-s3 font-medium text-ds-text hover:bg-ds-neutral-tint',
          )}
          aria-label={
            unread.length > 0
              ? t('nav.notifications_unread', {
                  count: unread.length,
                  defaultValue: '{{count}} ulæste notifikationer',
                })
              : t('nav.notifications', 'Notifikationer')
          }
        >
          <Bell className="h-5 w-5 text-ds-muted" aria-hidden />
          <span>{t('nav.notifications', 'Notifikationer')}</span>
          {unread.length > 0 ? (
            <span
              className={cn(
                TYPE.meta,
                'ml-auto inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-ds-coral-badge px-s1 text-white tabular-nums',
              )}
            >
              {unread.length}
            </span>
          ) : null}
        </button>
      }
    >
      {items.length === 0 ? (
        <p className={cn(TYPE.small, 'px-s3 py-s2 text-ds-muted')}>
          {t('nav.notifications_empty', 'Ingen notifikationer')}
        </p>
      ) : (
        items.map((row) => {
          const meta = notificationMetadata(row.metadata);
          const href = staffNotificationHref(meta);
          return (
            <MenuItem
              key={row.id}
              onClick={() => {
                if (!row.read_at) void markRead.mutateAsync(row.id);
                navigate(href);
              }}
            >
              <span className="flex min-w-0 items-center justify-between gap-s2">
                <span className={cn('truncate', !row.read_at && 'font-semibold')} title={row.body}>
                  {row.title}
                </span>
                <span className={cn(TYPE.caption, 'shrink-0 text-ds-placeholder')}>
                  {formatMessageDateTime(row.created_at, i18n.language)}
                </span>
              </span>
            </MenuItem>
          );
        })
      )}
    </MenuPopover>
  );
}
